/**
 * Tree-sitter helpers for controller/drivebase input-binding discovery.
 * Loads WASM from Vite asset URLs for browser use; hardwareParser calls this
 * on the parse-only path (button triggers and axis/variable controls).
 */
import { Language, Parser, Query, type Node, type Tree } from 'web-tree-sitter';
import treeSitterRuntimeWasmUrl from 'web-tree-sitter/web-tree-sitter.wasm?url';
import treeSitterPythonWasmUrl from 'tree-sitter-python/tree-sitter-python.wasm?url';
import { getFrameworkAxisActionInputArgNames, getFrameworkAxisActionNames } from '../framework/framework-axis-actions';
import { INPUT_BINDING_DEVICE, type InputBindingDevice } from './inputBindings';

const { CONTROLLER, DRIVEBASE } = INPUT_BINDING_DEVICE;

export type PythonTreeSitterWasmUrls = {
  /** URL to `web-tree-sitter.wasm` (Vite `?url` asset or same-origin path). */
  runtimeWasmUrl: string;
  /** URL to `tree-sitter-python.wasm`. */
  languageWasmUrl: string;
};

export const DEFAULT_PYTHON_TREE_SITTER_WASM_URLS: PythonTreeSitterWasmUrls = {
  runtimeWasmUrl: treeSitterRuntimeWasmUrl,
  languageWasmUrl: treeSitterPythonWasmUrl,
};

/** Intermediate IR aligned with hardwareParser `InputMappingTriggerCall` (+ receiver). */
export type TreeSitterInputBindingReceiver = {
  name: string;
  className: 'ActionController' | 'ActionDriveBasePanel';
  device: InputBindingDevice;
};

export type TreeSitterInputBindingTrigger = {
  receiverVarName: string;
  buttonName: string;
  triggerToken: string;
  rawActionExpression: string;
  rawCode: string;
  sourceLineNumber: number;
  startIndex: number;
  endIndex: number;
  /**
   * True when the call is not a plain module-level expression statement (nested under
   * `def` / `class` / compounds / other suites, or same-line wrappers like `return` /
   * assignment RHS). Hydrate treats these as unsupported (delete-only) so Control edits
   * cannot drop surrounding structure via whole-line mutation.
   */
  isWrapped: boolean;
};

export type TreeSitterAxisInputControlCall = {
  /** Controller var referenced by the axis getter (e.g. `controller`). */
  controllerVarName: string;
  actionPath: string;
  actionName: string;
  getterName: string;
  /** Remaining call args with the axis input kwarg removed (scanner-compatible tokens). */
  argsTokens: string[];
  sourceLineNumber: number;
  startIndex: number;
  endIndex: number;
  rawCode: string;
  /** See `TreeSitterInputBindingTrigger.isWrapped`. */
  isWrapped: boolean;
};

export type TreeSitterInputBindingIr = {
  receivers: TreeSitterInputBindingReceiver[];
  triggers: TreeSitterInputBindingTrigger[];
  axisCalls: TreeSitterAxisInputControlCall[];
};

const INPUT_BINDING_TRIGGER_TOKENS = new Set(['on_pressed', 'on_released', 'on_change', 'on_toggle']);
const CONTROLLER_CLASS_NAMES = new Set(['ActionController', 'ActionDriveBasePanel']);
const AXIS_GETTER_NAMES = new Set(['get_r1_axis', 'get_l1_axis', 'get_r2_axis', 'get_l2_axis']);

const BUTTON_TRIGGER_QUERY = `
(call
  function: (attribute
    object: (call
      function: (attribute
        object: (identifier) @receiver
        attribute: (identifier) @button)
      arguments: (argument_list))
    attribute: (identifier) @trigger)
  arguments: (argument_list) @args) @call
`;

const PLAIN_TRIGGER_QUERY = `
(call
  function: (attribute
    object: (identifier) @receiver
    attribute: (identifier) @trigger)
  arguments: (argument_list) @args) @call
`;

const RECEIVER_DECL_QUERY = `
(assignment
  left: (identifier) @name
  right: (call
    function: (identifier) @class
    arguments: (argument_list)))
`;

const AXIS_METHOD_CALL_QUERY = `
(call
  function: (attribute
    object: (_) @action_object
    attribute: (identifier) @action_name)
  arguments: (argument_list) @args) @call
`;

/**
 * Process-wide WASM caches for this Vitest/browser process (one editor session /
 * worker). `initPythonTreeSitter` loads `web-tree-sitter` + `tree-sitter-python`
 * once, then sync helpers reuse `cachedLanguage`, `cachedQueries`, and a single
 * shared `cachedParser` without re-fetching WASM or allocating a new parser.
 * Suites typically warm these in `beforeAll` via `createPythonTreeSitterParser()`.
 */
/** Shared `Parser.init` (runtime WASM) — cleared on reject so retries can re-run. */
let runtimeInitPromise: Promise<void> | null = null;
/**
 * In-flight full setup (runtime init + Language.load + queries + shared parser).
 * Concurrent callers with the same URL key await this instead of double-loading.
 */
let languageSetupPromise: Promise<Language> | null = null;
let languageSetupKey: string | null = null;
let cachedLanguage: Language | null = null;
let cachedUrlsKey: string | null = null;
let cachedQueries: { button: Query; plain: Query; decl: Query; axis: Query } | null = null;
/** Shared parser for the CodeEditor session / process; safe to reuse on the main thread. */
let cachedParser: Parser | null = null;

function ensureCachedParser(language: Language): Parser {
  if (cachedParser) {
    cachedParser.setLanguage(language);
    return cachedParser;
  }
  cachedParser = new Parser();
  cachedParser.setLanguage(language);
  return cachedParser;
}

function urlsKey(urls: PythonTreeSitterWasmUrls): string {
  return `${urls.runtimeWasmUrl}\0${urls.languageWasmUrl}`;
}

function isFullyInitializedForKey(key: string): boolean {
  return cachedLanguage !== null && cachedQueries !== null && cachedUrlsKey === key && cachedParser !== null;
}

function clearLanguageCaches(): void {
  cachedLanguage = null;
  cachedQueries = null;
  cachedUrlsKey = null;
}

function isBrowserHttpContext(): boolean {
  return typeof window !== 'undefined' && typeof window.location?.protocol === 'string' && window.location.protocol.startsWith('http');
}

/**
 * Resolve a Vite `?url` asset to something web-tree-sitter can load:
 * - Real browser page: absolute same-origin HTTP URL (fetch)
 * - Vitest/Node: absolute filesystem path (wasm glue uses Node fs; happy-dom is not enough)
 */
async function resolveWasmLoadTarget(url: string): Promise<string> {
  const inVitest = import.meta.env.MODE === 'test' || Boolean(import.meta.env.VITEST);
  const useBrowserFetch = !inVitest && isBrowserHttpContext();

  if (useBrowserFetch) {
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return new URL(url, window.location.origin).href;
  }

  // Node / Vitest filesystem target (avoid `node:path` so Vite does not externalize it for the browser bundle)
  let pathname = url;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    pathname = new URL(url).pathname;
  }
  if (pathname.startsWith('/')) {
    const cwd = typeof process !== 'undefined' && typeof process.cwd === 'function' ? process.cwd() : '';
    return `${cwd}${pathname}`;
  }
  if (pathname.startsWith('file:')) return pathname;
  return pathname;
}

/**
 * Initializes the WASM runtime and loads the Python grammar once per page/process.
 * Defaults to Vite-bundled wasm asset URLs for browser use.
 * Also ensures a shared `Parser` is ready for CodeEditor / sync binding helpers.
 * In Vitest, call this (or `createPythonTreeSitterParser`) from `beforeAll` so sync
 * range helpers and binding mutations see a ready parser for the rest of the file.
 *
 * Concurrent callers coalesce on one in-flight setup promise (runtime + Language.load
 * + queries). Rejected setups clear sticky state so a later retry can succeed.
 */
export async function initPythonTreeSitter(urls: PythonTreeSitterWasmUrls = DEFAULT_PYTHON_TREE_SITTER_WASM_URLS): Promise<Language> {
  const key = urlsKey(urls);
  if (isFullyInitializedForKey(key) && cachedLanguage) {
    ensureCachedParser(cachedLanguage);
    return cachedLanguage;
  }

  if (languageSetupPromise && languageSetupKey === key) {
    return languageSetupPromise;
  }

  if (languageSetupPromise) {
    try {
      await languageSetupPromise;
    } catch {
      // Prior attempt failed; fall through and start a new setup for this key.
    }
    if (isFullyInitializedForKey(key) && cachedLanguage) {
      ensureCachedParser(cachedLanguage);
      return cachedLanguage;
    }
  }

  languageSetupKey = key;
  languageSetupPromise = (async () => {
    try {
      const [runtimeTarget, languageTarget] = await Promise.all([
        resolveWasmLoadTarget(urls.runtimeWasmUrl),
        resolveWasmLoadTarget(urls.languageWasmUrl),
      ]);

      // Do not leave a rejected runtime init sticky: a failed load must allow retry.
      if (!runtimeInitPromise) {
        runtimeInitPromise = Parser.init({
          locateFile: () => runtimeTarget,
        }).catch((error) => {
          runtimeInitPromise = null;
          throw error;
        });
      }
      await runtimeInitPromise;

      const language = await Language.load(languageTarget);
      const queries = {
        button: new Query(language, BUTTON_TRIGGER_QUERY),
        plain: new Query(language, PLAIN_TRIGGER_QUERY),
        decl: new Query(language, RECEIVER_DECL_QUERY),
        axis: new Query(language, AXIS_METHOD_CALL_QUERY),
      };
      cachedLanguage = language;
      cachedUrlsKey = key;
      cachedQueries = queries;
      ensureCachedParser(language);
      return language;
    } catch (error) {
      clearLanguageCaches();
      throw error;
    }
  })().finally(() => {
    if (languageSetupKey === key) {
      languageSetupPromise = null;
      languageSetupKey = null;
    }
  });

  return languageSetupPromise;
}

/**
 * Returns the process-wide shared Python `Parser` (creating/initing caches if needed).
 * Prefer this over constructing parsers ad hoc so CodeEditor parse/mutate paths share one instance.
 */
export async function createPythonTreeSitterParser(urls: PythonTreeSitterWasmUrls = DEFAULT_PYTHON_TREE_SITTER_WASM_URLS): Promise<Parser> {
  const language = await initPythonTreeSitter(urls);
  return ensureCachedParser(language);
}

/**
 * Start shared WASM/parser init without requiring the caller to handle the Parser.
 * Safe to call repeatedly; shares process-wide caches with discovery/mutations.
 * Use on CodeEditor mount (and similar) so Control edits are less likely to race
 * the first hardware parse. Returns false when init fails (mutations stay fail-closed).
 *
 * Treats init itself as the readiness probe: on failure, retries with backoff so a
 * later successful load (transient network / CacheStorage) can recover without a
 * full remount. Hardware parse also re-attempts init on each run.
 */
export async function prewarmPythonTreeSitter(
  urls: PythonTreeSitterWasmUrls = DEFAULT_PYTHON_TREE_SITTER_WASM_URLS,
  options: { maxRetries?: number; retryDelaysMs?: readonly number[] } = {}
): Promise<boolean> {
  const maxRetries = options.maxRetries ?? 2;
  const retryDelaysMs = options.retryDelaysMs ?? [1000, 3000];

  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    if (attempt > 0) {
      const delayMs = retryDelaysMs[attempt - 1] ?? retryDelaysMs[retryDelaysMs.length - 1] ?? 1000;
      await new Promise<void>((resolve) => {
        const timeoutId = setTimeout(() => {
          clearTimeout(timeoutId);
          resolve();
        }, delayMs);
      });
    }
    try {
      await createPythonTreeSitterParser(urls);
      return true;
    } catch {
      // Probe again on the next attempt (or give up after maxRetries).
    }
  }
  return false;
}

/** Bumped on each not-ready → ready (or Retry) that must re-hydrate Control bindings. */
let pythonTreeSitterReadyTransitionGeneration = 0;
/** Last generation for which a hardware parse committed while Tree-sitter was ready. */
let pythonTreeSitterBindingsHydratedGeneration = 0;
/** True after a finished parse whose Tree-sitter binding discovery/hydrate failed. */
let pythonTreeSitterBindingsHydrateFailed = false;
const pythonTreeSitterReadyTransitionListeners = new Set<() => void>();
const pythonTreeSitterBindingsHydratedListeners = new Set<() => void>();

/**
 * True when Tree-sitter is ready and Control bindings have been hydrated for the
 * current ready-transition generation (mutations may unlock).
 */
export function arePythonTreeSitterBindingsHydratedForCurrentReady(): boolean {
  if (!isPythonTreeSitterReady()) return false;
  if (pythonTreeSitterBindingsHydrateFailed) return false;
  return pythonTreeSitterBindingsHydratedGeneration >= pythonTreeSitterReadyTransitionGeneration;
}

/** True when the last finished binding hydrate attempt failed (Retry can recover). */
export function isPythonTreeSitterBindingsHydrateFailed(): boolean {
  return pythonTreeSitterBindingsHydrateFailed;
}

/** Pages subscribe to bump their hardware-parse epoch after a ready transition / Retry. */
export function subscribePythonTreeSitterReadyTransition(listener: () => void): () => void {
  pythonTreeSitterReadyTransitionListeners.add(listener);
  return () => {
    pythonTreeSitterReadyTransitionListeners.delete(listener);
  };
}

/** UI hooks subscribe so mutation gates update when hydrate commits or fails. */
export function subscribePythonTreeSitterBindingsHydrated(listener: () => void): () => void {
  pythonTreeSitterBindingsHydratedListeners.add(listener);
  return () => {
    pythonTreeSitterBindingsHydratedListeners.delete(listener);
  };
}

function notifyBindingsHydrateListeners(): void {
  for (const listener of [...pythonTreeSitterBindingsHydratedListeners]) {
    listener();
  }
}

/**
 * Request a Control bindings re-hydrate: bump the ready-transition generation when
 * already caught up, then notify page listeners to reparse. Safe to call from Retry
 * after a successful prewarm (including when WASM was already warm from a side path).
 */
export function requestPythonTreeSitterBindingsHydrate(): void {
  if (!isPythonTreeSitterReady()) return;
  pythonTreeSitterBindingsHydrateFailed = false;
  if (pythonTreeSitterBindingsHydratedGeneration >= pythonTreeSitterReadyTransitionGeneration) {
    pythonTreeSitterReadyTransitionGeneration += 1;
  }
  for (const listener of [...pythonTreeSitterReadyTransitionListeners]) {
    listener();
  }
  notifyBindingsHydrateListeners();
}

/**
 * Mark that the current-source hardware parse successfully hydrated bindings while
 * Tree-sitter is ready. Unlocks Control mutations gated on hydrate-for-current-ready.
 */
export function notifyPythonTreeSitterBindingsHydrated(): void {
  if (!isPythonTreeSitterReady()) return;
  pythonTreeSitterBindingsHydrateFailed = false;
  pythonTreeSitterBindingsHydratedGeneration = pythonTreeSitterReadyTransitionGeneration;
  notifyBindingsHydrateListeners();
}

/**
 * Mark that binding discovery/hydrate failed for the current source. Keeps mutations
 * locked and surfaces Retry; does not bump the page parse epoch (avoids a fail loop).
 */
export function notifyPythonTreeSitterBindingsHydrateFailed(): void {
  if (!isPythonTreeSitterReady()) return;
  pythonTreeSitterBindingsHydrateFailed = true;
  if (pythonTreeSitterBindingsHydratedGeneration >= pythonTreeSitterReadyTransitionGeneration) {
    pythonTreeSitterReadyTransitionGeneration += 1;
  }
  notifyBindingsHydrateListeners();
}

export function parsePythonWithTreeSitter(parser: Parser, source: string): Tree {
  const tree = parser.parse(source);
  if (!tree) {
    throw new Error('web-tree-sitter returned a null tree');
  }
  return tree;
}

/**
 * Parse `source` and extract binding IR, deleting the temporary Tree afterward.
 * Prefer this over `parsePythonWithTreeSitter` + `extractInputBindingIrFromTree`
 * for production paths so WASM heap from trees does not accumulate.
 * Callers that keep a Tree (tests / ad-hoc inspection) must call `tree.delete()`.
 */
export function extractInputBindingIrFromSource(parser: Parser, source: string): TreeSitterInputBindingIr {
  const tree = parsePythonWithTreeSitter(parser, source);
  try {
    return extractInputBindingIrFromTree(tree, source);
  } finally {
    tree.delete();
  }
}

/**
 * Extract binding IR with the shared cached parser. Returns null when Tree-sitter is not ready.
 * Prefer this when a caller will match many bindings against one source snapshot.
 */
export function extractInputBindingIrFromSourceSync(code: string): TreeSitterInputBindingIr | null {
  if (!isPythonTreeSitterReady() || !cachedParser) return null;
  return extractInputBindingIrFromSource(cachedParser, code);
}

function getQueries(): { button: Query; plain: Query; decl: Query; axis: Query } {
  if (!cachedQueries) {
    throw new Error('pythonTreeSitterInputBindings: call initPythonTreeSitter / createPythonTreeSitterParser first');
  }
  return cachedQueries;
}

function getLineNumberAtOffset(code: string, offset: number): number {
  return code.slice(0, Math.max(0, offset)).split(/\r?\n/).length;
}

/** Match hardwareParser trailing-comment acceptance for rawCode spans. */
function readAcceptedCallTrailingSpan(code: string, closeIndex: number): { accepted: boolean; rawCodeEndIndex: number } {
  const lineEndIndex = code.indexOf('\n', closeIndex + 1);
  const trailingEndIndex = lineEndIndex === -1 ? code.length : lineEndIndex;
  const trailingText = code.slice(closeIndex + 1, trailingEndIndex).trim();
  if (!trailingText || trailingText.startsWith('#')) {
    return {
      accepted: true,
      rawCodeEndIndex: trailingText.startsWith('#') ? trailingEndIndex : closeIndex + 1,
    };
  }
  // Same-line semicolon suite (`call(); other()`): discover the call; siblings stay outside rawCode.
  if (trailingText.startsWith(';')) {
    return { accepted: true, rawCodeEndIndex: closeIndex + 1 };
  }
  return { accepted: false, rawCodeEndIndex: closeIndex + 1 };
}

/** Statement types whose inline (non-`block`) suite field may hold a binding call. */
const INLINE_SUITE_STATEMENT_TYPES = new Set([
  'if_statement',
  'for_statement',
  'while_statement',
  'with_statement',
  'try_statement',
  'match_statement',
]);

/**
 * True when the statement is joined to more code on the same line by `;`
 * (`binding(); log_ready()` or `binding();  # note`). A bare trailing `;` has no neighbor
 * to preserve, so it does not make the call unsupported.
 */
function hasSameLineSemicolonNeighbor(source: string, statementEndIndex: number): boolean {
  const lineEndIndex = source.indexOf('\n', statementEndIndex);
  const trailingText = source.slice(statementEndIndex, lineEndIndex === -1 ? source.length : lineEndIndex).trim();
  if (!trailingText.startsWith(';')) return false;
  return trailingText.slice(1).trim().length > 0;
}

/**
 * True when Control should treat the call as delete-only unsupported.
 * Editable bindings are only plain module-level expression statements that own their whole
 * line (`controller.a().on_pressed(...)` at top level). Anything nested under `def` /
 * `class` / compounds / other suites, wrapped on the same line, or joined to neighbors in a
 * semicolon suite is unsupported, because Control mutates whole lines.
 */
function isWrappedBindingCall(callNode: Node, source: string): boolean {
  if (callNode.parent?.type !== 'expression_statement') return true;

  const stmt = callNode.parent;
  // Tree-sitter puts top-level statements directly under `module`; any other parent means
  // the call lives inside a suite (def/class/if/for/except/…)—not Control-editable shape.
  if (stmt.parent?.type !== 'module') return true;

  const lineStart = source.lastIndexOf('\n', Math.max(0, callNode.startIndex - 1)) + 1;
  const prefix = source.slice(lineStart, callNode.startIndex);
  if (!/^[ \t]*$/.test(prefix)) return true;

  // First statement of a semicolon suite: the prefix is blank, but a whole-line edit would
  // still drop the statements after the `;`.
  return hasSameLineSemicolonNeighbor(source, Math.max(stmt.endIndex, callNode.endIndex));
}

function argumentListInnerText(argsNode: Node): string {
  const text = argsNode.text;
  if (text.startsWith('(') && text.endsWith(')')) {
    return text.slice(1, -1);
  }
  return text;
}

function capturesByName(captures: { name: string; node: Node }[]): Map<string, Node> {
  const map = new Map<string, Node>();
  for (const capture of captures) {
    map.set(capture.name, capture.node);
  }
  return map;
}

function deviceForClassName(className: string): InputBindingDevice | null {
  if (className === 'ActionController') return CONTROLLER;
  if (className === 'ActionDriveBasePanel') return DRIVEBASE;
  return null;
}

function attributePathText(node: Node): string {
  if (node.type === 'identifier') return node.text;
  if (node.type === 'attribute') {
    const objectNode = node.childForFieldName('object');
    const attributeNode = node.childForFieldName('attribute');
    if (!objectNode || !attributeNode) return node.text;
    return `${attributePathText(objectNode)}.${attributeNode.text}`;
  }
  return node.text;
}

function readAxisGetter(valueNode: Node | null): { controllerVarName: string; getterName: string } | null {
  if (!valueNode || valueNode.type !== 'attribute') return null;
  const objectNode = valueNode.childForFieldName('object');
  const attributeNode = valueNode.childForFieldName('attribute');
  if (!objectNode || objectNode.type !== 'identifier' || !attributeNode) return null;
  const getterName = attributeNode.text;
  if (!AXIS_GETTER_NAMES.has(getterName)) return null;
  return { controllerVarName: objectNode.text, getterName };
}

function collectAxisArgsTokens(
  argsNode: Node,
  inputArgName: string
): { getter: { controllerVarName: string; getterName: string } | null; argsTokens: string[] } {
  let getter: { controllerVarName: string; getterName: string } | null = null;
  const argsTokens: string[] = [];
  let positionalGetter: { controllerVarName: string; getterName: string } | null = null;
  let positionalGetterSeen = false;

  for (let i = 0; i < argsNode.namedChildCount; i += 1) {
    const child = argsNode.namedChild(i);
    if (!child || child.type === 'comment') continue;

    if (child.type === 'keyword_argument') {
      const nameNode = child.childForFieldName('name');
      const valueNode = child.childForFieldName('value');
      const name = nameNode?.text ?? '';
      if (name === inputArgName) {
        getter = readAxisGetter(valueNode);
        continue;
      }
      argsTokens.push(child.text);
      continue;
    }

    // Positional form / mixed form: first positional axis getter fills the input arg slot
    // (e.g. `add_variable_velocity_control(controller.get_l1_axis, 50)`).
    if (!positionalGetterSeen) {
      const asGetter = readAxisGetter(child);
      if (asGetter) {
        positionalGetter = asGetter;
        positionalGetterSeen = true;
        continue;
      }
      positionalGetterSeen = true;
    }

    argsTokens.push(child.text);
  }

  return { getter: getter ?? positionalGetter, argsTokens };
}

/**
 * Build input-binding discovery IR from a Tree-sitter Python tree.
 * Shape mirrors hardwareParser trigger discovery (not full action resolution).
 */
export function extractInputBindingIrFromTree(tree: Tree, source: string): TreeSitterInputBindingIr {
  const queries = getQueries();
  const receivers: TreeSitterInputBindingReceiver[] = [];
  const knownReceivers = new Set<string>();

  for (const match of queries.decl.matches(tree.rootNode)) {
    const caps = capturesByName(match.captures);
    const name = caps.get('name')?.text;
    const className = caps.get('class')?.text;
    if (!name || !className || !CONTROLLER_CLASS_NAMES.has(className)) continue;
    const device = deviceForClassName(className);
    if (!device) continue;
    if (knownReceivers.has(name)) continue;
    knownReceivers.add(name);
    receivers.push({ name, className: className as TreeSitterInputBindingReceiver['className'], device });
  }

  const triggers: TreeSitterInputBindingTrigger[] = [];
  const seenSpans = new Set<string>();

  const pushTrigger = (opts: { receiverVarName: string; buttonName: string; triggerToken: string; callNode: Node; argsNode: Node }) => {
    const triggerToken = opts.triggerToken.toLowerCase();
    if (!INPUT_BINDING_TRIGGER_TOKENS.has(triggerToken)) return;

    const closeIndex = opts.callNode.endIndex - 1;
    const trailingSpan = readAcceptedCallTrailingSpan(source, closeIndex);
    if (!trailingSpan.accepted) return;

    const startIndex = opts.callNode.startIndex;
    const endIndex = trailingSpan.rawCodeEndIndex;
    const spanKey = `${startIndex}:${endIndex}:${opts.receiverVarName}:${opts.buttonName}:${triggerToken}`;
    if (seenSpans.has(spanKey)) return;
    seenSpans.add(spanKey);

    triggers.push({
      receiverVarName: opts.receiverVarName,
      buttonName: opts.buttonName,
      triggerToken,
      rawActionExpression: argumentListInnerText(opts.argsNode),
      rawCode: source.slice(startIndex, endIndex),
      sourceLineNumber: getLineNumberAtOffset(source, startIndex),
      startIndex,
      endIndex,
      isWrapped: isWrappedBindingCall(opts.callNode, source),
    });
  };

  for (const match of queries.button.matches(tree.rootNode)) {
    const caps = capturesByName(match.captures);
    const receiver = caps.get('receiver');
    const button = caps.get('button');
    const trigger = caps.get('trigger');
    const call = caps.get('call');
    const args = caps.get('args');
    if (!receiver || !button || !trigger || !call || !args) continue;
    pushTrigger({
      receiverVarName: receiver.text,
      buttonName: button.text,
      triggerToken: trigger.text,
      callNode: call,
      argsNode: args,
    });
  }

  for (const match of queries.plain.matches(tree.rootNode)) {
    const caps = capturesByName(match.captures);
    const receiver = caps.get('receiver');
    const trigger = caps.get('trigger');
    const call = caps.get('call');
    const args = caps.get('args');
    if (!receiver || !trigger || !call || !args) continue;
    // Skip `controller.a()` style button accessors; those are not trigger bindings.
    if (!INPUT_BINDING_TRIGGER_TOKENS.has(trigger.text.toLowerCase())) continue;
    pushTrigger({
      receiverVarName: receiver.text,
      buttonName: '',
      triggerToken: trigger.text,
      callNode: call,
      argsNode: args,
    });
  }

  triggers.sort((a, b) => a.startIndex - b.startIndex || a.endIndex - b.endIndex);

  const axisActionNames = getFrameworkAxisActionNames();
  const inputArgByAction = getFrameworkAxisActionInputArgNames();
  const axisCalls: TreeSitterAxisInputControlCall[] = [];
  const seenAxisSpans = new Set<string>();

  for (const match of queries.axis.matches(tree.rootNode)) {
    const caps = capturesByName(match.captures);
    const actionObject = caps.get('action_object');
    const actionNameNode = caps.get('action_name');
    const call = caps.get('call');
    const args = caps.get('args');
    if (!actionObject || !actionNameNode || !call || !args) continue;

    const actionName = actionNameNode.text;
    if (!axisActionNames.has(actionName)) continue;
    const inputArgName = inputArgByAction.get(actionName);
    if (!inputArgName) continue;

    const closeIndex = call.endIndex - 1;
    const trailingSpan = readAcceptedCallTrailingSpan(source, closeIndex);
    if (!trailingSpan.accepted) continue;

    const { getter, argsTokens } = collectAxisArgsTokens(args, inputArgName);
    if (!getter) continue;

    const startIndex = call.startIndex;
    const endIndex = trailingSpan.rawCodeEndIndex;
    const actionPath = attributePathText(actionObject);
    const spanKey = `${startIndex}:${endIndex}:${getter.controllerVarName}:${actionPath}:${actionName}:${getter.getterName}`;
    if (seenAxisSpans.has(spanKey)) continue;
    seenAxisSpans.add(spanKey);

    axisCalls.push({
      controllerVarName: getter.controllerVarName,
      actionPath,
      actionName,
      getterName: getter.getterName,
      argsTokens,
      sourceLineNumber: getLineNumberAtOffset(source, startIndex),
      startIndex,
      endIndex,
      rawCode: source.slice(startIndex, endIndex),
      isWrapped: isWrappedBindingCall(call, source),
    });
  }

  axisCalls.sort((a, b) => a.startIndex - b.startIndex || a.endIndex - b.endIndex);
  return { receivers, triggers, axisCalls };
}

/** Shape aligned with hardwareParser `InputMappingTriggerCall`. */
export type InputMappingTriggerCallLike = {
  buttonName: string;
  triggerToken: string;
  rawActionExpression: string;
  rawCode: string;
  sourceLineNumber: number;
  isWrapped: boolean;
};

export type TreeSitterInputBindingDiscovery = {
  /** `ActionController` / `ActionDriveBasePanel` assignments (supports renamed vars). */
  receivers: TreeSitterInputBindingReceiver[];
  triggersByReceiver: Map<string, InputMappingTriggerCallLike[]>;
  /** Axis/variable control calls keyed by controller var used in the getter. */
  axisCallsByController: Map<string, TreeSitterAxisInputControlCall[]>;
};

/**
 * Parse `code` once for controller/drivebase receivers, button triggers, and axis controls.
 * Used by hardwareParser when Tree-sitter input-binding discovery is enabled.
 */
export async function loadInputBindingDiscoveryFromTreeSitter(
  code: string,
  urls: PythonTreeSitterWasmUrls = DEFAULT_PYTHON_TREE_SITTER_WASM_URLS
): Promise<TreeSitterInputBindingDiscovery> {
  const parser = await createPythonTreeSitterParser(urls);
  const ir = extractInputBindingIrFromSource(parser, code);
  const triggersByReceiver = new Map<string, InputMappingTriggerCallLike[]>();
  const axisCallsByController = new Map<string, TreeSitterAxisInputControlCall[]>();

  for (const trigger of ir.triggers) {
    const list = triggersByReceiver.get(trigger.receiverVarName) ?? [];
    list.push({
      buttonName: trigger.buttonName,
      triggerToken: trigger.triggerToken,
      rawActionExpression: trigger.rawActionExpression,
      rawCode: trigger.rawCode,
      sourceLineNumber: trigger.sourceLineNumber,
      isWrapped: trigger.isWrapped,
    });
    triggersByReceiver.set(trigger.receiverVarName, list);
  }

  for (const axisCall of ir.axisCalls) {
    const list = axisCallsByController.get(axisCall.controllerVarName) ?? [];
    list.push(axisCall);
    axisCallsByController.set(axisCall.controllerVarName, list);
  }

  return { receivers: ir.receivers, triggersByReceiver, axisCallsByController };
}

export type TreeSitterSourceLineRange = {
  /** 1-based inclusive line numbers (Monaco-compatible). */
  startLine: number;
  endLine: number;
  startIndex?: number;
  endIndex?: number;
};

/** Monaco edit for deleting an unsupported / wrapped binding (may replace sole body with `pass`). */
export type TreeSitterBindingDeleteEdit = {
  startLine: number;
  endLine: number;
  startColumn: number;
  endColumn: number;
  text: string;
};

/**
 * True once this process has successfully loaded the Tree-sitter WASM runtime,
 * Python grammar, binding queries, and the shared parser. False before the first
 * init (or after a test-only cache reset). Sync discovery/mutation helpers treat
 * not-ready as fail-closed (empty ranges / no scanner fallback).
 */
export function isPythonTreeSitterReady(): boolean {
  return cachedLanguage !== null && cachedQueries !== null && cachedParser !== null;
}

/**
 * Test-only: clear the process-wide language/query/parser caches without unloading
 * WASM from the engine. Use this to exercise the not-ready fail-closed path after a
 * suite has already warmed Tree-sitter in `beforeAll`.
 *
 * Always restore afterward with `await createPythonTreeSitterParser()` (prefer
 * `try` / `finally`) so later tests in the same Vitest worker still see a ready
 * parser. Do not call from production code.
 */
export function resetPythonTreeSitterCachesForTests(): void {
  if (cachedParser) {
    cachedParser.delete();
    cachedParser = null;
  }
  clearLanguageCaches();
  // Allow a fresh Parser.init / full setup after a prior rejection (or after cache reset).
  // Successful WASM runtime state in the engine may still be warm; that is intentional.
  runtimeInitPromise = null;
  languageSetupPromise = null;
  languageSetupKey = null;
  pythonTreeSitterReadyTransitionGeneration = 0;
  pythonTreeSitterBindingsHydratedGeneration = 0;
  pythonTreeSitterBindingsHydrateFailed = false;
}

/**
 * Synchronously parse `code` and return line ranges for axis/variable calls matching
 * controller + getter (any action). Requires a prior successful `initPythonTreeSitter`.
 * Returns [] when Tree-sitter is not ready.
 */
export function findAxisBindingLineRangesForGetterSync(code: string, controllerVarName: string, getterName: string): TreeSitterSourceLineRange[] {
  if (!isPythonTreeSitterReady() || !cachedParser) return [];

  const ir = extractInputBindingIrFromSource(cachedParser, code);
  return ir.axisCalls
    .filter((call) => call.controllerVarName === controllerVarName && call.getterName === getterName)
    .map((call) => ({
      startLine: call.sourceLineNumber,
      endLine: getLineNumberAtOffset(code, Math.max(0, call.endIndex - 1)),
    }));
}

/**
 * Synchronously parse `code` and return line ranges for axis/variable calls matching
 * controller + action + getter. Requires a prior successful `initPythonTreeSitter`.
 * Returns [] when Tree-sitter is not ready.
 */
export function findVariableAxisBindingLineRangesSync(
  code: string,
  controllerVarName: string,
  criteria: { actionQualified: string; getterName: string },
  ir?: TreeSitterInputBindingIr | null
): TreeSitterSourceLineRange[] {
  if (!isPythonTreeSitterReady() || !cachedParser) return [];

  const resolvedIr = ir ?? extractInputBindingIrFromSource(cachedParser, code);
  const expectedQualified = criteria.actionQualified.trim();

  return resolvedIr.axisCalls
    .filter((call) => {
      if (call.controllerVarName !== controllerVarName) return false;
      if (call.getterName !== criteria.getterName) return false;
      const qualified = `${call.actionPath}.${call.actionName}`;
      return qualified === expectedQualified;
    })
    .map((call) => ({
      startLine: call.sourceLineNumber,
      endLine: getLineNumberAtOffset(code, Math.max(0, call.endIndex - 1)),
    }));
}

/**
 * Synchronously parse `code` and return line ranges for button/drivebase trigger calls
 * matching receiver + button + trigger token. Requires prior `initPythonTreeSitter`.
 * Returns [] when Tree-sitter is not ready.
 *
 * Pass `ir` from `extractInputBindingIrFromSourceSync` to avoid re-parsing when matching
 * many bindings against the same source.
 */
export function findButtonTriggerBindingLineRangesSync(
  code: string,
  receiverVarName: string,
  criteria: { buttonName: string; triggerToken: string },
  ir?: TreeSitterInputBindingIr | null
): TreeSitterSourceLineRange[] {
  if (!isPythonTreeSitterReady() || !cachedParser) return [];

  const resolvedIr = ir ?? extractInputBindingIrFromSource(cachedParser, code);
  const expectedButton = criteria.buttonName.trim().toLowerCase();
  const expectedTrigger = criteria.triggerToken.trim().toLowerCase();

  return resolvedIr.triggers
    .filter((trigger) => {
      if (trigger.receiverVarName !== receiverVarName) return false;
      if (trigger.triggerToken !== expectedTrigger) return false;
      return trigger.buttonName.trim().toLowerCase() === expectedButton;
    })
    .map((trigger) => ({
      startLine: trigger.sourceLineNumber,
      endLine: getLineNumberAtOffset(code, Math.max(0, trigger.endIndex - 1)),
    }));
}

/**
 * True when `receiverVarName` still has button triggers (and optionally axis controls)
 * outside `ignoreLineNumbers` (1-based). Prefer calling this on the **post-delete** source with
 * an empty ignore set when deciding whether to remove the panel declaration — ignoring whole
 * lines hides surviving same-line neighbours after a partial-span delete.
 *
 * When Tree-sitter is not ready, returns true (fail closed: keep the declaration).
 */
export function hasRemainingInputBindingsForReceiverSync(
  code: string,
  receiverVarName: string,
  options: { includeAxisMappings: boolean; ignoreLineNumbers: ReadonlySet<number> }
): boolean {
  if (!isPythonTreeSitterReady() || !cachedParser) return true;

  const ir = extractInputBindingIrFromSource(cachedParser, code);

  const spanHasRemainingLine = (startLine: number, endLine: number): boolean => {
    for (let line = startLine; line <= endLine; line++) {
      if (!options.ignoreLineNumbers.has(line)) return true;
    }
    return false;
  };

  for (const trigger of ir.triggers) {
    if (trigger.receiverVarName !== receiverVarName) continue;
    const endLine = getLineNumberAtOffset(code, Math.max(0, trigger.endIndex - 1));
    if (spanHasRemainingLine(trigger.sourceLineNumber, endLine)) return true;
  }

  if (options.includeAxisMappings) {
    for (const axisCall of ir.axisCalls) {
      if (axisCall.controllerVarName !== receiverVarName) continue;
      const endLine = getLineNumberAtOffset(code, Math.max(0, axisCall.endIndex - 1));
      if (spanHasRemainingLine(axisCall.sourceLineNumber, endLine)) return true;
    }
  }

  return false;
}

/**
 * Highest 1-based end line of top-level button triggers for `receiverVarName`
 * (and optionally top-level axis controls for `axisControllerVarName`) whose
 * start line is in `(afterLine, maxLine]`. Nested/indented calls are ignored so
 * inserts stay with the editable mapping block. Used to append new mappings.
 *
 * Only non-wrapped (plain module-level) bindings are considered, matching hydrate's
 * editable set — nested / same-line wrappers do not extend the insert block.
 *
 * Returns null when Tree-sitter is not ready or no mappings fall in that window
 * (caller keeps insert-after-declaration).
 */
export function findLastInputBindingEndLineForReceiverSync(
  code: string,
  options: {
    receiverVarName: string;
    axisControllerVarName?: string | null;
    afterLine: number;
    maxLine: number;
  }
): number | null {
  if (!isPythonTreeSitterReady() || !cachedParser) return null;

  const ir = extractInputBindingIrFromSource(cachedParser, code);
  let lastEndLine: number | null = null;

  const consider = (startLine: number, endIndex: number, isWrapped: boolean) => {
    // Align with hydrate: only plain module-level bindings anchor the insert block.
    if (isWrapped) return;
    if (startLine <= options.afterLine || startLine > options.maxLine) return;
    const endLine = getLineNumberAtOffset(code, Math.max(0, endIndex - 1));
    if (lastEndLine === null || endLine > lastEndLine) lastEndLine = endLine;
  };

  for (const trigger of ir.triggers) {
    if (trigger.receiverVarName !== options.receiverVarName) continue;
    consider(trigger.sourceLineNumber, trigger.endIndex, trigger.isWrapped);
  }

  const axisVar = options.axisControllerVarName?.trim();
  if (axisVar) {
    for (const axisCall of ir.axisCalls) {
      if (axisCall.controllerVarName !== axisVar) continue;
      consider(axisCall.sourceLineNumber, axisCall.endIndex, axisCall.isWrapped);
    }
  }

  return lastEndLine;
}

const AXIS_GETTER_TO_BUTTON: Record<string, string> = {
  get_r1_axis: 'r1',
  get_l1_axis: 'l1',
  get_r2_axis: 'r2',
  get_l2_axis: 'l2',
};

/**
 * Line ranges for unsupported button/drivebase trigger calls and axis/variable controls
 * matching `receiverVarName` and trimmed `rawCode`. Tree-sitter discovery already ignores
 * string/docstring bodies.
 *
 * When `sourceLineNumber` is set, only a call starting on that line is returned
 * (anchored fail-closed if the span no longer matches). Otherwise all raw matches
 * are returned (caller typically deletes the first).
 *
 * When `triggerToken` is set (e.g. `on_pressed`), only button/drivebase triggers with that
 * token are considered. When omitted, both triggers and axis calls may match (used for
 * `variable` unsupported rows that cover axis controls and `on_change`).
 *
 * Returns [] when Tree-sitter is not ready.
 */
export function findUnsupportedBindingLineRangesSync(
  code: string,
  receiverVarName: string,
  criteria: {
    rawCode: string;
    buttonName?: string;
    triggerToken?: string;
    sourceLineNumber?: number;
  },
  ir?: TreeSitterInputBindingIr | null
): TreeSitterSourceLineRange[] {
  if (!isPythonTreeSitterReady() || !cachedParser) return [];

  const expectedRaw = criteria.rawCode.trim();
  if (!expectedRaw) return [];

  const resolvedIr = ir ?? extractInputBindingIrFromSource(cachedParser, code);
  const expectedButton = criteria.buttonName?.trim().toLowerCase();
  const expectedTrigger = criteria.triggerToken?.trim().toLowerCase();

  const ranges: TreeSitterSourceLineRange[] = [];

  for (const trigger of resolvedIr.triggers) {
    if (trigger.receiverVarName !== receiverVarName) continue;
    if (expectedButton && trigger.buttonName.trim().toLowerCase() !== expectedButton) continue;
    if (expectedTrigger && trigger.triggerToken !== expectedTrigger) continue;
    if (criteria.sourceLineNumber !== undefined && trigger.sourceLineNumber !== criteria.sourceLineNumber) continue;
    if (trigger.rawCode.trim() !== expectedRaw) continue;
    ranges.push({
      startLine: trigger.sourceLineNumber,
      endLine: getLineNumberAtOffset(code, Math.max(0, trigger.endIndex - 1)),
      startIndex: trigger.startIndex,
      endIndex: trigger.endIndex,
    });
  }

  // Axis/variable controls only when not filtering to a specific button trigger token.
  if (!expectedTrigger) {
    for (const axisCall of resolvedIr.axisCalls) {
      if (axisCall.controllerVarName !== receiverVarName) continue;
      if (expectedButton) {
        const axisButton = AXIS_GETTER_TO_BUTTON[axisCall.getterName];
        if (!axisButton || axisButton !== expectedButton) continue;
      }
      if (criteria.sourceLineNumber !== undefined && axisCall.sourceLineNumber !== criteria.sourceLineNumber) continue;
      if (axisCall.rawCode.trim() !== expectedRaw) continue;
      ranges.push({
        startLine: axisCall.sourceLineNumber,
        endLine: getLineNumberAtOffset(code, Math.max(0, axisCall.endIndex - 1)),
        startIndex: axisCall.startIndex,
        endIndex: axisCall.endIndex,
      });
    }
  }

  return ranges;
}

function offsetToMonacoPosition(code: string, offset: number): { lineNumber: number; column: number } {
  const safeOffset = Math.max(0, Math.min(offset, code.length));
  const lineStart = code.lastIndexOf('\n', Math.max(0, safeOffset - 1)) + 1;
  return {
    lineNumber: getLineNumberAtOffset(code, safeOffset === 0 ? 0 : safeOffset),
    column: safeOffset - lineStart + 1,
  };
}

function indexesToDeleteEdit(code: string, startIndex: number, endIndex: number, text: string): TreeSitterBindingDeleteEdit {
  const start = offsetToMonacoPosition(code, startIndex);
  const end = offsetToMonacoPosition(code, endIndex);
  return {
    startLine: start.lineNumber,
    endLine: end.lineNumber,
    startColumn: start.column,
    endColumn: end.column,
    text,
  };
}

function namedChildren(node: Node): Node[] {
  const children: Node[] = [];
  for (let i = 0; i < node.namedChildCount; i += 1) {
    const child = node.namedChild(i);
    if (child) children.push(child);
  }
  return children;
}

function isPythonStatementNode(node: Node): boolean {
  const type = node.type;
  return (
    type === 'expression_statement' ||
    type === 'return_statement' ||
    type === 'pass_statement' ||
    type === 'break_statement' ||
    type === 'continue_statement' ||
    type === 'raise_statement' ||
    type === 'assert_statement' ||
    type === 'delete_statement' ||
    type === 'global_statement' ||
    type === 'nonlocal_statement' ||
    type === 'import_statement' ||
    type === 'import_from_statement' ||
    type === 'if_statement' ||
    type === 'for_statement' ||
    type === 'while_statement' ||
    type === 'with_statement' ||
    type === 'try_statement' ||
    type === 'match_statement' ||
    type === 'function_definition' ||
    type === 'class_definition' ||
    type === 'decorated_definition'
  );
}

function sameNode(left: Node, right: Node): boolean {
  return left.startIndex === right.startIndex && left.endIndex === right.endIndex && left.type === right.type;
}

function findDescendantCallAt(node: Node, startIndex: number): Node | null {
  if (node.type === 'call' && node.startIndex === startIndex) return node;
  for (const child of namedChildren(node)) {
    if (startIndex < child.startIndex || startIndex >= child.endIndex) continue;
    const found = findDescendantCallAt(child, startIndex);
    if (found) return found;
  }
  return null;
}

function findEnclosingStatement(callNode: Node): Node | null {
  let stmt: Node | null = callNode.parent;
  while (stmt && !isPythonStatementNode(stmt)) {
    stmt = stmt.parent;
  }
  return stmt;
}

function extendIndexPastTrailingNewline(source: string, endIndex: number): number {
  if (endIndex < source.length && source[endIndex] === '\r' && source[endIndex + 1] === '\n') return endIndex + 2;
  if (endIndex < source.length && source[endIndex] === '\n') return endIndex + 1;
  return endIndex;
}

/**
 * When `stmt` is the only statement in a suite body, return how to replace it with `pass`.
 * Covers compounds, `def` / `class`, `except` / `finally` / `case`, etc.
 * Inline (tree-sitter often aliases a same-line suite as `block`): replace the whole suite
 * statement (`return …`, assignment, or bare call) with `pass` so Python stays valid.
 * Indented block: replace the whole indented statement line with `pass`.
 */
function soleSuiteBodyPassEdit(stmt: Node, callRawEndIndex: number, source: string): TreeSitterBindingDeleteEdit | null {
  const parent = stmt.parent;
  if (!parent) return null;

  if (parent.type === 'block') {
    const bodyStmts = namedChildren(parent).filter(isPythonStatementNode);
    if (bodyStmts.length !== 1 || !bodyStmts[0] || !sameNode(bodyStmts[0], stmt)) return null;

    const lineStart = source.lastIndexOf('\n', Math.max(0, stmt.startIndex - 1)) + 1;
    const prefixBeforeStmt = source.slice(lineStart, stmt.startIndex);
    const stmtEnd = Math.max(stmt.endIndex, callRawEndIndex);
    // Same-line suite (`if x: return call` / `if x: call`): keep the header, replace the
    // whole suite statement (not only the binding call) with `pass`.
    if (!/^[ \t]*$/.test(prefixBeforeStmt)) {
      return indexesToDeleteEdit(source, stmt.startIndex, stmtEnd, 'pass');
    }

    const endIndex = extendIndexPastTrailingNewline(source, stmtEnd);
    const indent = prefixBeforeStmt.match(/^[ \t]*/)?.[0] ?? '';
    return indexesToDeleteEdit(source, lineStart, endIndex, `${indent}pass\n`);
  }

  if (INLINE_SUITE_STATEMENT_TYPES.has(parent.type)) {
    const body = parent.childForFieldName('consequence') ?? parent.childForFieldName('body');
    if (!body || !sameNode(body, stmt)) return null;
    // Inline suite without a block wrapper: replace the whole suite statement with `pass`.
    return indexesToDeleteEdit(source, stmt.startIndex, Math.max(stmt.endIndex, callRawEndIndex), 'pass');
  }

  return null;
}

function wholeStatementDeleteEdit(stmt: Node, callRawEndIndex: number, source: string): TreeSitterBindingDeleteEdit {
  let startIndex = stmt.startIndex;
  // Prefer the discovered raw end so parser-accepted trailing comments on the call are removed.
  let endIndex = Math.max(stmt.endIndex, callRawEndIndex);
  const lineStart = source.lastIndexOf('\n', Math.max(0, startIndex - 1)) + 1;
  let lineEnd = source.indexOf('\n', endIndex);
  if (lineEnd === -1) lineEnd = source.length;

  const beforeOnLine = source.slice(lineStart, startIndex);
  const leadingSemiMatch = beforeOnLine.match(/;[ \t]*$/);
  if (leadingSemiMatch) {
    // Middle/last in `a(); binding(); c()` — drop the preceding `;` so neighbors stay joined.
    startIndex -= leadingSemiMatch[0].length;
    return indexesToDeleteEdit(source, startIndex, endIndex, '');
  }

  const afterOnLine = source.slice(endIndex, lineEnd);
  const trailingSemiMatch = afterOnLine.match(/^[ \t]*;/);
  if (trailingSemiMatch) {
    // First in `binding(); other()` — drop the following `;` (and one run of spaces).
    endIndex += trailingSemiMatch[0].length;
    const followingSpace = source.slice(endIndex, lineEnd).match(/^[ \t]+/);
    if (followingSpace) endIndex += followingSpace[0].length;
    return indexesToDeleteEdit(source, startIndex, endIndex, '');
  }

  const prefix = source.slice(lineStart, stmt.startIndex);
  if (/^[ \t]*$/.test(prefix)) startIndex = lineStart;
  endIndex = extendIndexPastTrailingNewline(source, endIndex);
  return indexesToDeleteEdit(source, startIndex, endIndex, '');
}

/**
 * Build a Monaco delete/replace edit for a binding call span.
 * Sole body of a suite (`if` / `def` / …) → `pass`; otherwise delete the enclosing statement.
 */
export function resolveBindingDeleteEditForCall(callNode: Node, callRawEndIndex: number, source: string): TreeSitterBindingDeleteEdit {
  const stmt = findEnclosingStatement(callNode);
  if (!stmt) {
    return indexesToDeleteEdit(source, callNode.startIndex, callRawEndIndex, '');
  }

  const passEdit = soleSuiteBodyPassEdit(stmt, callRawEndIndex, source);
  if (passEdit) return passEdit;

  return wholeStatementDeleteEdit(stmt, callRawEndIndex, source);
}

/**
 * Resolve delete (or `pass`) edit for an unsupported binding matched by rawCode.
 * Returns null when Tree-sitter is not ready, no matching call exists, or the call
 * node cannot be resolved (fail closed — do not wipe lines without `pass` injection).
 */
export function resolveUnsupportedBindingDeleteEditSync(
  code: string,
  receiverVarName: string,
  criteria: {
    rawCode: string;
    buttonName?: string;
    triggerToken?: string;
    sourceLineNumber?: number;
  }
): TreeSitterBindingDeleteEdit | null {
  if (!isPythonTreeSitterReady() || !cachedParser) return null;

  const ranges = findUnsupportedBindingLineRangesSync(code, receiverVarName, criteria);
  const range = ranges[0];
  if (!range || range.startIndex === undefined || range.endIndex === undefined) return null;

  const tree = parsePythonWithTreeSitter(cachedParser, code);
  try {
    const callNode = findDescendantCallAt(tree.rootNode, range.startIndex);
    if (!callNode) return null;
    return resolveBindingDeleteEditForCall(callNode, range.endIndex, code);
  } finally {
    tree.delete();
  }
}
